import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../context/UserContext";
import { useTranslation } from "react-i18next";
import type { ChecklistReportType, ChecklistSubitemReport } from "../../types/reports/types";
import AttToken from "../../helpers/attToken";
import Tickets, { novaMensagem, objectModel, RejectedTicketPayload, TicketsV3 } from "../../service/Tickets";
import ModalDeny from "./ModalDeny";
import { toast } from "react-toastify";
import ModalDetailPrint from "../Reports/Modal/ModalDetailPrint";
import ModalImageOnTicket from "./ModalImageOnTicket";
import RedirectModal from "./Modals/RedirectModal";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Skeleton, Stack, TextField, Typography } from "@mui/material";
import ImagesCmp from "../ImagesCmp";
import type { ItemChecklist } from "../../types/checklist/types";
import { useParams } from "react-router-dom";
import { trackEventMatomo } from "../../helpers/matomo";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import LowPriorityIcon from "@mui/icons-material/LowPriority";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import ImagesCmpAssinatura from "../ImagesCmpAssinatura";
import ChamadosDuplicados from "./ChamadosDuplicados";
import queryClient from "../../service/query";
import { useMutation, useQuery } from "@tanstack/react-query";
import EmAndamento from "./EmAndamento";
import ListaArquivos from "../Reports/Modal/ListaArquivos";
import { uploadFilesV2 } from "../helpers/checklists";
import type { TicketFormContext, TicketsType } from "../../types/notifications/types";
import NovoChamado from "../../service/NovoChamado";
import { Reportsv2 } from "../../service/Reports";
import { getTicketStatusPresentation } from "../../helpers/ticketStatusPresentation";

const ticketsService = new Tickets();
const ticketsV3Service = new TicketsV3();
const ticketModelsService = new NovoChamado();
const reportsV2Service = new Reportsv2();

const getReturnedTicketStatus = (response: unknown): string | undefined => {
  if (!response || typeof response !== "object") return undefined;

  const record = response as Record<string, unknown>;
  if (typeof record.status === "string") return record.status;

  const data = record.data;
  return data && typeof data === "object" && typeof (data as Record<string, unknown>).status === "string"
    ? (data as Record<string, unknown>).status as string
    : undefined;
};

type ActionOption = {
  id: string;
  label: string;
};

type TicketFileReference = {
  nome: string;
  type: string;
  path: string;
};

const getTicketImageReferences = (references: unknown): string[] => {
  if (Array.isArray(references)) {
    return references.filter((reference): reference is string => typeof reference === "string" && Boolean(reference));
  }

  return typeof references === "string"
    ? references.split(",").filter(Boolean)
    : [];
};

const getTicketFileReferences = (references: unknown): TicketFileReference[] => {
  if (Array.isArray(references)) {
    return references.filter((reference): reference is TicketFileReference =>
      typeof reference === "object" && reference !== null && "path" in reference && "nome" in reference,
    );
  }

  return typeof references === "string"
    ? references.split(",").filter(Boolean).map((path) => ({
      path,
      nome: path.split("/").pop() || path,
      type: "",
    }))
    : [];
};

const getCompleteTicketId = (ticketId: string, companyId?: string): string => {
  if (!companyId || ticketId.includes("_") || ticketId.startsWith('-')) return ticketId;
  return `${ticketId}_${companyId}`;
};

const getInitialActionStatus = (): novaMensagem => {
  const now = new Date().getTime();

  return {
    comentario: "",
    dataAcompanhamento: new Date(now - (now % 86400000) + 10800000).getTime(),
    status: "emandamento",
    referenciaFoto: [],
    referenciaArquivo: [],
  };
};

const formatDate = (value: unknown): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  return new Date(value).toLocaleString("pt-BR");
};

export const actionOptions: ActionOption[] = [
  { id: "accept", label: "notifications.ticket.actions.accept" },
  { id: "approve", label: "notifications.ticket.actions.approve" },
  { id: "start", label: "notifications.ticket.actions.start" },
  { id: "reject", label: "notifications.ticket.actions.reject" },
  { id: "complete", label: "notifications.ticket.actions.complete" },
  { id: "cancel", label: "notifications.ticket.actions.cancel" },
  { id: "close", label: "notifications.ticket.actions.close" },
  { id: "reopen", label: "notifications.ticket.actions.reopen" },
  { id: "comment", label: "notifications.ticket.actions.comment" },
  { id: "priority", label: "notifications.ticket.actions.priority" },
];

const availableActionKeys: Record<string, string> = {
  accept: "accept",
  approve: "approve",
  start: "start_progress",
  reject: "reject",
  cancel: "cancel",
  complete: "complete",
  close: "close",
  reopen: "reopen",
  priority: "change_priority",
  comment: "comment",
};

export default function SingleNotification({
  idModal,
  mutate,
  requesterMode = false,
  onActionPendingChange,
  onActionSuccess,
}: {
  idModal?: string;
  mutate?: Function;
  requesterMode?: boolean;
  onActionPendingChange?: (pending: boolean) => void;
  onActionSuccess?: () => void;
}) {
  const { t } = useTranslation("translation");

  const idParam = useParams();
  const chamadoId = (idParam.id as string) || (idModal as string);

  const { hierarchyLabel, setPopUp, userData, popUp } = useContext(UserContext);

  const [selected, setSelected] = useState<ChecklistReportType>();
  const [images, setImages] = useState<File[]>([]);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [openModalPrint, setOpenModalPrint] = useState(false);
  const [openRefuse, setOpenRefuse] = useState(false);
  const [openImgPreview, setOpenImgPreview] = useState(false);
  const [openRedirect, setOpenRedirect] = useState(false);
  const [isRedirectPending, setIsRedirectPending] = useState(false);
  const [openNewTicket, setOpenNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState<RejectedTicketPayload>({ modeloId: "" });
  const [openDispute, setOpenDispute] = useState(false);
  const [disputeComment, setDisputeComment] = useState("");
  const [imgPreview] = useState("");
  const [objectStatus, setObjectStatus] = useState<novaMensagem>(getInitialActionStatus);
  const [action, setAction] = useState<string>("");
  const [priority, setPriority] = useState("");
  const [isLoadingFormDetails, setIsLoadingFormDetails] = useState(false);

  const resetActionForm = () => {
    setObjectStatus(getInitialActionStatus());
    setImages([]);
    setArquivos([]);
    setPriority("");
    setAction("");
  };

  type ActionRequest = { actionParam: string; motivo?: string };

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ["GET_TICKET_BY_ID", (idParam.id as string) ?? (idModal as string)],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const id = getCompleteTicketId(chamadoId, token.userEmpresa);
        const response = await ticketsService.getTicketById(token, id);
        if (!response) throw new Error("Resposta vazia");
        return response;
      }
    },
    retry: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const isActionAvailable = useCallback((actionParam: string) => {
    return data?.acoesDisponiveis?.[availableActionKeys[actionParam] ?? actionParam] === true;
  }, [data?.acoesDisponiveis]);

  const availableActionOptions = requesterMode
    ? []
    : actionOptions.reduce<ActionOption[]>((availableOptions, option) => {
      const isAlreadyListed = availableOptions.some(availableOption => availableOption.id === option.id);
      return isActionAvailable(option.id) && !isAlreadyListed
        ? [...availableOptions, option]
        : availableOptions;
    }, []);

  const canAcceptRejected = requesterMode && data?.status === "recusado" && data?.acoesDisponiveis?.accept_reject === true;
  const canDisputeRejected = requesterMode && data?.status === "recusado" && data?.acoesDisponiveis?.dispute_reject === true;
  const canCreateNewTicket = requesterMode && data?.status === "recusado" && data?.acoesDisponiveis?.new_ticket_from_reject === true;
  const canReopenCancelled = requesterMode && data?.status === "cancelado" && data?.acoesDisponiveis?.reopen === true;

  useEffect(() => {
    if (action && !isActionAvailable(action)) setAction("");
  }, [action, isActionAvailable]);

  const { data: ticketModels, isLoading: isLoadingTicketModels } = useQuery({
    queryKey: ["GET_REQUESTER_NEW_TICKET_MODELS"],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");
      const response = await ticketModelsService.getList(token);
      if (!response) throw new Error("Erro ao buscar modelos de chamado");
      return response as { id: string; name: string }[];
    },
    enabled: openNewTicket,
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isError) {
    toast.error(t("notifications.errorFetchingNotifications") as string);
  }

  const formContext = data?.formContext as TicketFormContext | null | undefined;
  const item = data?.execucaoChecklist?.itens?.find((item: ItemChecklist) => item.id === data.itemId);

  const obterDadosOrigem = () => {
    if (!item) return { subitem: null, nomeOcorrencia: '' };
    
    // Se o item for de ocorrência múltipla, buscamos na lista de ocorrências
    if(item.ocorrenciaMultipla && item.ocorrencias) {
      const occurrenceById = item.ocorrencias.find((ocorrencia: any) => String(ocorrencia.id) === String(data?.ocorrenciaId));
      if (occurrenceById) {
        return {
          subitem: occurrenceById.subitens?.find((sub: any) => sub.id === data?.subitemId) || null,
          nomeOcorrencia: occurrenceById.nome || '',
        };
      }

      for (const ocorrencia of item.ocorrencias) {
        const subitemAlvo = ocorrencia.subitens?.find((sub: any) => sub.id === data?.subitemId);
        
        if (subitemAlvo) {
          // Verifica se este subitem específico contém o chamado que gerou a página atual
          const possuiChamado = subitemAlvo.chamados?.some((chamado: any) => 
            chamado.textChamado === data.instrucao &&
            chamado.dateChamado === data.dataPrevisao &&
            chamado.prioridade === data.prioridade &&
            chamado.selKey === data.modeloId
          );
          if (possuiChamado) {
            return { subitem: subitemAlvo, nomeOcorrencia: ocorrencia.nome };
          }
        };
      };

      // Caso não encontre uma correspondência exata, retorna o primeiro como fallback
      const subitem = item.ocorrencias
        ?.flatMap((oco: any) => oco.subitens || [])
        ?.find((sub: any) => sub.id === data?.subitemId);
      
      const nomeOcorrencia = item.ocorrencias
        ?.find((oco: any) => oco.subitens
        ?.some((sub: any) => sub.id === data?.subitemId))?.nome || '';

      return { subitem, nomeOcorrencia }; 
    }

    // Para itens normais, busca diretamente nos subitens
    const subitem = item.subitens?.find((sub: any) => sub.id === data?.subitemId);
    return { subitem, nomeOcorrencia: '' };
  };

  const { subitem, nomeOcorrencia } = obterDadosOrigem();

  useEffect(() => {
    if (isSuccess) {
      setSelected({
        ...data?.execucaoChecklist,
        cliente: data?.nomeCliente ?? data?.cliente,
        local: data?.nomeLocal ?? data?.local,
        conjunto: data?.nomeConjunto ?? data?.conjunto,
        unidade: data?.nomeUnidade ?? data?.unidade,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, isSuccess]);

  const verificaResponsavel = () => {
    if (data?.responsavelNome && data?.responsavelNome !== userData?.userName) {
      return false;
    } else return true;
  };

  const closeImgPreview = async () => {
    setOpenImgPreview(false);
  };

  const closeRedirectModal = async () => {
    setOpenRedirect(false);
  };

  const onHandleClick = () => {
    setPopUp(!popUp);
    setOpenModalPrint(!openModalPrint);
  };

  const openFormDetails = async () => {
    const inicio = formContext?.execucaoInicio ?? data?.execucaoChecklist?.inicio;
    if (typeof inicio !== "number") {
      toast.error("Não foi possível identificar a execução deste chamado.");
      return;
    }

    const token = await AttToken();
    if (!token) return;

    setIsLoadingFormDetails(true);
    try {
      const response = await reportsV2Service.getDetailsForTicket(token, inicio, data?.formAnonimoId);
      const checklist = response.execucoes?.[0];
      if (!checklist) {
        toast.error("Não foi possível carregar os detalhes do formulário.");
        return;
      }

      setSelected(checklist);
      setPopUp(!popUp);
      setOpenModalPrint(true);
    } catch {
      // O interceptor da API já apresenta a mensagem devolvida pelo serviço.
    } finally {
      setIsLoadingFormDetails(false);
    }
  };

  const comentario = (param: ChecklistSubitemReport) => {
    if (param?.tipo === "multiple" || param?.tipo === "avaliacao") {
      return (
        param?.ocorrencia && (
          <>
            <Typography fontSize={13} color="textDisabled" fontWeight={700} component="p">
              {t("notifications.ticket.commentLabel")}
              <Typography fontSize={13} component="span">
                {" "}
                {param?.ocorrencia}
              </Typography>
            </Typography>
          </>
        )
      );
    } else
      return (
        param?.obs && (
          <>
            <Typography fontSize={13} color="textDisabled" fontWeight={700} component="p">
              {t("notifications.ticket.commentLabel")}
              <Typography fontSize={13} component="span">
                {" "}
                {param?.obs}
              </Typography>
            </Typography>
          </>
        )
      );
  };

  const renderSwitch = (param: ChecklistSubitemReport) => {
    switch (param.tipo) {
      case "multiple":
      case "unique":
        return (
          param?.selecao?.filter((e, i) => param?.selecionados && param?.selecionados[i])
            ?.map((e, i) => {
              return (
                <Typography
                  sx={{ marginLeft: "2px" }}
                  key={i}
                  fontWeight={700}
                  fontSize={13}
                  component="tr">
                  {e}
                </Typography>
              );
            })
        );
      case "avaliacao": {
        const avaliacao = typeof param.nota === "number" ? param.avaliacao?.[param.nota] : null;
        return avaliacao ? (
          <Typography sx={{ marginLeft: "2px" }} fontWeight={700} fontSize={13} component="span">
            {avaliacao.nome}
          </Typography>
        ) : null;
      }
      case "list":
        return param?.selecionados?.map((e, i) => {
          return (
            <Typography key={i} fontSize={13} component="tr">
              {" "}
              {e.texto}
            </Typography>
          );
        });
      case "data":
      case "hora":
      case "email":
      case "telefone":
      case "rg":
      case "cpfcnpj":
      case "cep":
      case "placa":
      case "textoLongo":
      case "textoCurto":
        return (
          param.ocorrencia && (
            <Typography fontSize={13} component="span">
              {" "}
              {param.ocorrencia}
            </Typography>
          )
        );
      case "assinatura":
        return <ImagesCmpAssinatura images={[param.assinatura as string]} selected={{} as ChecklistReportType} />;
    }
  };

  async function convertFiles(files: File[], chamadoId: string) {
    const arquivosConvertidos: File[] = [];
    const referencias: objectModel[] = [];

    for (const file of files) {
      const fileType = file.name.substring(file.name.lastIndexOf(".") + 1);
      const newName = `${crypto.randomUUID()}.${fileType}`;
      const newFile = new File([file], newName, { type: file.type });

      arquivosConvertidos.push(newFile);
      referencias.push({
        path: `chamados/${chamadoId}/${newName}`,
        nome: file.name,
        type: file.type,
      });
    }

    return { arquivosConvertidos, referencias };
  }

  async function convertImages(files: File[], chamadoId: string) {
    const imagensConvertidas: File[] = [];
    const referencias: string[] = [];

    for (const file of files) {
      const newName = `${crypto.randomUUID()}.webp`;
      const newFile = new File([file], newName, { type: file.type });

      imagensConvertidas.push(newFile);
      referencias.push(`chamados/${chamadoId}/${newName}`);
    }

    return { imagensConvertidas, referencias };
  }

  const { mutate: executeAction, isPending } = useMutation({
    mutationKey: ["TICKET_ACTION"],
    mutationFn: async ({ actionParam, motivo }: ActionRequest) => toast.promise((async () => {
      if (requesterMode || !isActionAvailable(actionParam)) {
        throw new Error("Usuário sem permissão para atualizar este chamado");
      }
      const token = await AttToken();
      if (!token || !idParam) throw new Error("Token inválido ou id ausente");
      const allFiles: File[] = [];
      const referenciaArquivo: objectModel[] = [];
      const referenciaFoto: string[] = [];

      if (arquivos?.length) {
        const { arquivosConvertidos, referencias } = await convertFiles(arquivos, data.id);
        allFiles.push(...arquivosConvertidos);
        referenciaArquivo.push(...referencias);
      }

      if (images?.length) {
        const { imagensConvertidas, referencias } = await convertImages(images, data.id);
        allFiles.push(...imagensConvertidas);
        referenciaFoto.push(...referencias);
      }

      if (allFiles.length > 0) {
        await uploadFilesV2(allFiles, `chamados/${data.id}`);
      }

      const response = await ticketsV3Service.action(
        token,
        getCompleteTicketId(data?.id || chamadoId, token.userEmpresa),
        actionParam,
        objectStatus.comentario,
        actionParam === "priority" ? priority : data?.prioridade,
        objectStatus.dataAcompanhamento as number,
        referenciaFoto.join(","),
        referenciaArquivo.map(file => file.path).join(","),
        motivo,
      );
      if (!response) throw new Error("Resposta vazia");
      return response;
    })(), {
      pending: t("notifications.ticket.actionPending"),
      success: {
        render({ data: response }) {
          const presentation = getTicketStatusPresentation(getReturnedTicketStatus(response));
          return presentation ? t(presentation.labelKey) : t("notifications.ticket.actionSuccess");
        },
      },
      error: t("notifications.ticket.actionError"),
    }, { toastId: `ticket-action-${actionParam}` }),
    onSuccess: () => {
      resetActionForm();
      queryClient.resetQueries({ queryKey: ["GET_TICKET_BY_ID"] });
      queryClient.refetchQueries({ queryKey: ["GET_CHAMADOS_SOLICITANTE"] });
      queryClient.refetchQueries({ queryKey: ["GET_TICKETS"] });
      mutate && mutate();
      setOpenRefuse(false);
      onActionSuccess?.();
    },
  });

  // const getStatus = (value: string) => {
  //   switch (value) {
  //     case "fechadoDuplicidade":
  //       return (
  //         <span
  //           className="tag is-danger"
  //           style={{
  //             height: "auto",
  //             textAlign: "center",
  //             whiteSpace: "normal",
  //             fontWeight: 700,
  //             padding: "10px",
  //           }}>
  //           Fechado por duplicidade
  //         </span>
  //       );
  //     case "emandamento":
  //     case "aceito":
  //     case "cancelado":
  //     case "concluido":
  //     case "fechado":
  //     case "reaberto":
  //       return data.historicoChamado ? (
  //         Object.keys(data.historicoChamado)?.map((e) => (
  //           <>
  //             <Stack width="100%" direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
  //               <Stack width="50%" direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
  //                 <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" spacing={0.5} paddingLeft={1.2}>
  //                   <Typography variant="subtitle1" fontWeight={600} fontSize="large">
  //                     {(data?.historicoChamado as any)[e].nomeUsuarioAcompanhamento || ""}
  //                   </Typography>
  //                   <Typography variant="caption" fontWeight={600} fontSize="small">
  //                     {t("notifications.ticket.Data_de _atendimento")}:&nbsp;
  //                     <Typography variant="caption" fontSize="small">
  //                       {typeof (data?.historicoChamado as any)[e].data === "number"
  //                         ? new Date((data?.historicoChamado as any)[e].data).toLocaleString("pt-BR")
  //                         : (data?.historicoChamado as any)[e].data || "-"}
  //                     </Typography>
  //                   </Typography>
  //                   <Typography variant="caption" fontWeight={600} fontSize="small">
  //                     {t("notifications.ticket.Comentario")}:&nbsp;
  //                     <Typography variant="caption" fontSize="small">
  //                       {(data?.historicoChamado as any)[e].comentario || "-"}
  //                     </Typography>
  //                   </Typography>
  //                   <Typography variant="caption" fontWeight={600} fontSize="small">
  //                     {t("status")}:&nbsp;
  //                     <Typography variant="caption" fontSize="small">
  //                       {(data?.historicoChamado as any)[e].statusTraduzido || "-"}
  //                     </Typography>
  //                   </Typography>
  //                 </Stack>
  //                 <Typography variant="subtitle2" fontSize={"12px"}>
  //                   Atualizado em:{" "}
  //                   {typeof (data?.historicoChamado as any)[e].dataMensagem === "number"
  //                     ? new Date((data?.historicoChamado as any)[e].dataMensagem).toLocaleString("pt-BR")
  //                     : (data?.historicoChamado as any)[e].dataMensagem || "-"}
  //                 </Typography>
  //               </Stack>
  //               <Stack width="50%" direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
  //                 {getTicketImageReferences(data?.historicoChamado[e]?.foto).length > 0 && <ImagesCmp images={getTicketImageReferences(data?.historicoChamado[e]?.foto)} />}
  //                 {getTicketFileReferences(data?.historicoChamado[e]?.arquivo).length > 0 && (
  //                   <>
  //                     <Typography variant="subtitle1" fontWeight={600} color="textDisabled" fontSize="small">
  //                       Arquivos:
  //                     </Typography>
  //                     <ListaArquivos files={getTicketFileReferences(data?.historicoChamado[e]?.arquivo)} />
  //                   </>
  //                 )}
  //               </Stack>
  //             </Stack>
  //             <Divider sx={{ width: "100%" }} />
  //           </>
  //         ))
  //       ) : (
  //         <span style={{ fontStyle: "italic" }}>{t("notifications.ticket.chamado_sem_msg")}</span>
  //       );
  //     case "pendente":
  //       return <span style={{ fontStyle: "italic" }}>{t("notifications.ticket.chamado_pendente")}</span>;
  //     case "recusado":
  //       return (
  //         <span style={{ fontStyle: "italic" }}>
  //           {t("notifications.ticket.Chamado_recusado")} por {data?.usuarioNome} em {new Date(data?.dataAlteracao)?.toLocaleString("pt-BR")}
  //         </span>
  //       );
  //     default:
  //       return <></>;
  //   }
  // };
  const { mutate: createFromRejected, isPending: isCreatingNewTicket } = useMutation({
    mutationKey: ["CREATE_TICKET_FROM_REJECTED"],
    mutationFn: async () => toast.promise((async () => {
      if (!canCreateNewTicket) {
        throw new Error("Solicitante sem permissão para abrir um novo chamado a partir deste status");
      }
      if (!newTicket.modeloId) {
        throw new Error("Modelo do novo chamado não selecionado");
      }

      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");

      const originalTicketId = getCompleteTicketId(chamadoId, token.userEmpresa);
      const response = await ticketsV3Service.createFromRejected(token, originalTicketId, newTicket);
      if (!response) throw new Error("Resposta vazia");
      return response;
    })(), {
      pending: t("notifications.requester.creatingNewTicket"),
      success: t("notifications.requester.newTicketCreated"),
      error: t("notifications.requester.errorCreatingNewTicket"),
    }, { toastId: "create-ticket-from-rejected" }),
    onSuccess: () => {
      setOpenNewTicket(false);
      setNewTicket({ modeloId: "" });
      onActionSuccess?.();
      queryClient.resetQueries({ queryKey: ["GET_TICKET_BY_ID"] });
      queryClient.refetchQueries({ queryKey: ["GET_CHAMADOS_SOLICITANTE"] });
      mutate?.();
    },
  });

  const { mutate: acceptRejected, isPending: isAcceptingRejected } = useMutation({
    mutationKey: ["ACCEPT_REJECTED_TICKET"],
    mutationFn: async () => toast.promise((async () => {
      if (!canAcceptRejected) {
        throw new Error("Solicitante sem permissão para aceitar este status");
      }

      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");

      const originalTicketId = getCompleteTicketId(chamadoId, token.userEmpresa);
      const response = await ticketsV3Service.acceptRejected(token, originalTicketId);
      if (!response) throw new Error("Resposta vazia");
      return response;
    })(), {
      pending: t("notifications.requester.acceptingRejection"),
      success: t("notifications.requester.rejectionAccepted"),
      error: t("notifications.requester.errorAcceptingRejection"),
    }, { toastId: "accept-rejected-ticket" }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GET_TICKET_BY_ID"] });
      queryClient.refetchQueries({ queryKey: ["GET_CHAMADOS_SOLICITANTE"] });
      mutate?.();
      onActionSuccess?.();
    },
  });

  const { mutate: disputeRejected, isPending: isDisputingRejected } = useMutation({
    mutationKey: ["DISPUTE_REJECTED_TICKET"],
    mutationFn: async () => toast.promise((async () => {
      if (!canDisputeRejected) {
        throw new Error("Solicitante sem permissão para contestar este chamado");
      }
      if (!disputeComment.trim()) {
        throw new Error("Comentário obrigatório");
      }

      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");

      const originalTicketId = getCompleteTicketId(chamadoId, token.userEmpresa);
      const response = await ticketsV3Service.disputeRejected(token, originalTicketId, disputeComment.trim());
      if (!response) throw new Error("Resposta vazia");
      return response;
    })(), {
      pending: t("notifications.requester.disputingRejection"),
      success: t("notifications.requester.rejectionDisputed"),
      error: t("notifications.requester.errorDisputingRejection"),
    }, { toastId: "dispute-rejected-ticket" }),
    onSuccess: () => {
      setDisputeComment("");
      setOpenDispute(false);
      onActionSuccess?.();
      queryClient.resetQueries({ queryKey: ["GET_TICKET_BY_ID"] });
      queryClient.refetchQueries({ queryKey: ["GET_CHAMADOS_SOLICITANTE"] });
      mutate?.();
    },
  });

  const { mutate: reopenCancelled, isPending: isReopeningCancelled } = useMutation({
    mutationKey: ["REOPEN_CANCELLED_TICKET"],
    mutationFn: async () => toast.promise((async () => {
      if (!canReopenCancelled) {
        throw new Error("Solicitante sem permissÃ£o para reabrir este chamado");
      }

      const token = await AttToken();
      if (!token) throw new Error("Token nÃ£o encontrado");

      const response = await ticketsV3Service.action(
        token,
        getCompleteTicketId(data?.id || chamadoId, token.userEmpresa),
        "reopen",
      );
      if (!response) throw new Error("Resposta vazia");
      return response;
    })(), {
      pending: t("notifications.requester.reopeningCancelled"),
      success: t("notifications.requester.cancelledReopened"),
      error: t("notifications.requester.errorReopeningCancelled"),
    }, { toastId: "reopen-cancelled-ticket" }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GET_TICKET_BY_ID"] });
      queryClient.refetchQueries({ queryKey: ["GET_CHAMADOS_SOLICITANTE"] });
      mutate?.();
      onActionSuccess?.();
    },
  });

  const isActionMutationPending = isPending || isCreatingNewTicket || isAcceptingRejected || isDisputingRejected || isReopeningCancelled || isRedirectPending;

  useEffect(() => {
    onActionPendingChange?.(isActionMutationPending);
  }, [isActionMutationPending, onActionPendingChange]);

  const tipoExibido = (value: string) => {
    switch (value) {
      case "avaliacao":
        return "(Avaliação)";
      case "multiple":
        return "(Multiplo)";
      case "unique":
        return "(Unico)";
      case "textoCurto":
        return "(Texto curto)";
      case "textoLongo":
        return "(Texto longo)";
      case "list":
        return "(Lista de textos)";
      case "email":
        return "(E-mail)";
      case "placa":
        return "(Placa)";
      case "cpfcnpj":
        return "(CPF / CNPJ)";
      case "cep":
        return "(CEP)";
      case "assinatura":
        return "(Assinatura)";
      case "rg":
        return "(RG)";
      case "data":
        return "(Data)";
      case "hora":
        return "(Hora)";
      case "telefone":
        return "(Telefone)";
      case "numero":
        return "(Numero)";
      default:
        return <div>AAAAAAA</div>;
    }
  };

  const ActionButtons = () => {
    if (requesterMode || (!availableActionOptions.length && !data?.encaminha)) return null;

    const icons: Record<string, JSX.Element> = {
      accept: <TaskAltIcon />,
      start: <PlayArrowIcon />,
      reject: <DoDisturbIcon />,
      complete: <CheckCircleOutlineIcon />,
      cancel: <CancelOutlinedIcon />,
      close: <LockOutlinedIcon />,
      reopen: <ReplayIcon />,
      approve: <TaskAltIcon />,
      comment: <CommentOutlinedIcon />,
      priority: <LowPriorityIcon />,
    };
    const colors: Record<string, "primary" | "success" | "error" | "warning"> = {
      accept: "success", start: "primary", reject: "error", complete: "success",
      cancel: "error", close: "primary", reopen: "warning", approve: "success", comment: "primary", priority: "primary",
    };
    const requiresDetails = (actionId: string) => ["complete", "cancel", "comment", "priority"].includes(actionId);

    return (
      <Stack spacing={1} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, padding: 1.5, backgroundColor: "action.hover" }}>
        <Typography variant="subtitle2" fontWeight={700}>{t("notifications.ticket.availableActions")}</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {data?.encaminha && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SubdirectoryArrowRightIcon />}
              disabled={isActionMutationPending || data?.block}
              onClick={() => {
                trackEventMatomo('chamado', 'click', 'botão', 'abre redirecionamento');
                setOpenRedirect(true);
              }}
            >
              {t("notifications.modals.redirect")}
            </Button>
          )}
          {availableActionOptions.map(option => (
            <Button
              key={option.id}
              size="small"
              variant={action === option.id ? "contained" : "outlined"}
              color={colors[option.id]}
              startIcon={icons[option.id]}
              disabled={isPending || data?.block || (option.id === "reject" && data?.blockRefuse)}
              onClick={() => {
                trackEventMatomo('chamado', 'click', 'ação', option.id);
                if (option.id === "reject") {
                  setOpenRefuse(true);
                  return;
                }
                if (requiresDetails(option.id)) {
                  if (option.id === "priority") setPriority(data?.prioridade ?? "");
                  setAction(option.id);
                  return;
                }
                executeAction({ actionParam: option.id });
              }}
            >
              {t(option.label)}
            </Button>
          ))}
        </Stack>
      </Stack>
    );
  };

  const History = () => {
    const entries = Object.values(data?.historicoChamado ?? {}) as Array<any>;
    const statusMessage = (() => {
      switch (data?.status) {
        case "fechadoDuplicidade":
          return t("notifications.myTickets.closedDueToDuplicity");
        case "pendente":
          return t("notifications.ticket.chamado_pendente");
        case "recusado":
          return `${t("notifications.ticket.Chamado_recusado")} por ${data?.usuarioNome || "-"} em ${new Date(data?.dataAlteracao).toLocaleString("pt-BR")}`;
        default:
          return null;
      }
    })();

    if (!entries.length) {
      return statusMessage ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {statusMessage}
        </Typography>
      ) : null;
    }

    return (
      <Stack spacing={1.25}>
        <Typography variant="subtitle1" fontWeight={700}>{t("notifications.ticket.Lista_acoes")}</Typography>
        {statusMessage && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {statusMessage}
          </Typography>
        )}
        {entries
          .sort((first, second) => (second.dataMensagem || second.data || 0) - (first.dataMensagem || first.data || 0))
          .map((history, index) => (
            <Stack key={history.id || index} spacing={1} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, padding: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} flexWrap="wrap">
                <Stack spacing={0.25}>
                  <Typography fontWeight={700}>{history.nomeUsuarioAcompanhamento || "-"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Data de atendimento: {typeof history.data === "number" ? new Date(history.data).toLocaleDateString("pt-BR") : history.data || "-"}
                  </Typography>
                </Stack>
                <Stack alignItems="flex-end" spacing={0.5}>
                  {history.statusTraduzido && <Chip size="small" label={history.statusTraduzido} color={getTicketStatusPresentation(history.status)?.chipColor ?? 'default'} />}
                </Stack>
              </Stack>
              {history.comentario && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{history.comentario}</Typography>}
              {getTicketImageReferences(history.foto).length > 0 && <ImagesCmp images={getTicketImageReferences(history.foto)} />}
              {getTicketFileReferences(history.arquivo).length > 0 && <ListaArquivos files={getTicketFileReferences(history.arquivo)} />}
              <Typography variant="caption" color="text.secondary" alignSelf="flex-end">
                {typeof history.dataMensagem === "number" ? new Date(history.dataMensagem).toLocaleString("pt-BR") : history.dataMensagem || "-"}
              </Typography>
            </Stack>
          ))}
      </Stack>
    );
  };

  const componentRef = useRef(null);

  const handleAfterPrint = useCallback(() => {
    console.log("`onAfterPrint` called");
  }, []);

  const handleBeforePrint = useCallback(() => {
    console.log("`onBeforePrint` called");
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    pageStyle: {
      padding: "20px",
    },
    contentRef: componentRef,
    documentTitle: selected?.nome,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const Loading = () => {
    return (
      <Stack width="100%" direction="column" justifyContent="center" alignItems="stretch" spacing={1}>
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <Skeleton variant="rounded" width={"200px"} height={40} />
          <Skeleton variant="rounded" width={"200px"} height={40} />
        </Stack>
        <Stack spacing={2} width={1000} direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start">
            <Skeleton variant="text" width={350} height={30} />
            {Array.from({ length: 9 }).map((_, index) => (
              <Skeleton key={index} height={20} width={250} />
            ))}
          </Stack>
          <Stack width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start">
            <Skeleton variant="text" width={350} height={30} />
            {Array.from({ length: 9 }).map((_, index) => (
              <Skeleton key={index} height={20} width={250} />
            ))}
          </Stack>
        </Stack>
        <Skeleton variant="rounded" height={"200px"} width="100%" />
      </Stack>
    );
  };

  const Buttons = () => {
    return (
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <Button
          variant="contained"
          endIcon={<FileOpenIcon />}
          disabled={isLoadingFormDetails}
          onClick={() => {
            trackEventMatomo("modal chamado", "click", "button", "fecha/cancela");
            void openFormDetails();
          }}>
          {isLoadingFormDetails ? "Carregando formulário..." : t("ticketDetail.showForm")}
        </Button>
        {data.originalId && (!data.duplicado || data.blockDuplicates === true) && (
          <Button
            onClick={() => {
              trackEventMatomo('chamado', 'click', 'botão', 'abre chamado original');
              window.open(process.env.REACT_APP_AXYMA_FRONT_URL + "detalheChamado/" + data?.originalId.split("_")[0]);
            }}
            variant="contained"
            color="secondary"
            endIcon={<SubdirectoryArrowRightIcon />}>
            Ver chamado original
          </Button>
        )}
        <Button
          variant="contained"
          color="success"
          endIcon={<PrintIcon />}
          onClick={(e) => {
            e.preventDefault();
            trackEventMatomo("Modal de visualização de form", "click", "button", "imprime");
            printFn();
          }}>
          {t("checklists.printConfig.Imprimir")}
        </Button>
      </Stack>
    );
  };

  const HeaderContent = () => {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={0.5}>
          <Typography variant="h6">{t("notifications.ticket.ticketInformation")}</Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            ID:
            <Typography fontSize={13} component="span">
              {" "}
              {data?.id}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.user")}
            <Typography fontSize={13} component="span">
              {" "}
              {data?.usuarioNome?.startsWith("anonimo") ? "Anonimo" : data?.usuarioNome}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("notifications.ticket.responsible")}
            <Typography fontSize={13} component="span">
              {" "}
              {data?.responsavelNome || "-"}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.dateIn")}
            <Typography fontSize={13} component="span">
              {" "}
              {formatDate(data?.inicio)}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.dateLimit")}
            <Typography
              fontSize={13}
              component="span"
              sx={{
                color: data && data?.dataPrevisao > Number(objectStatus?.dataAcompanhamento) ? "" : "red",
              }}>
              {" "}
              {formatDate(data?.dataPrevisao)}
            </Typography>
          </Typography>
          {data?.expirado && (
            <Chip size="small" color="error" label={t("notifications.ticket.expired")} />
          )}
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("status")}:
            <Typography fontSize={13} component="span">
              {" "}
              {getTicketStatusPresentation(data?.status) ? t(getTicketStatusPresentation(data?.status)?.labelKey as string) : data?.statusFormatado}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            Prioridade:
            <Typography fontSize={13} component="span">
              {" "}
              {(data.prioridade && data?.prioridade[0]?.toUpperCase() + data?.prioridade?.substring(1)) || "-"}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.message")}
            <Typography fontSize={13} component="span">
              {" "}
              {data?.instrucao}{" "}
            </Typography>
          </Typography>
        </Stack>
        <Stack width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start">
          <Typography variant="h6">{t("notifications.ticket.formInformation")}</Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.fromName")}
            <Typography fontSize={13} component="span">
              {" "}
              {formContext?.formularioNome || selected?.formulario || "-"}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.initial")}
            <Typography fontSize={13} component="span">
              {formatDate(formContext?.execucaoInicio ?? selected?.inicio)}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.final")}
            <Typography fontSize={13} component="span">
              {" "}
              {formatDate(formContext?.execucaoFim ?? selected?.fim)}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {t("ticketDetail.manage")}
            <Typography fontSize={13} component="span">
              {" "}
              {data?.nomeGerencia}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {hierarchyLabel.cliente}:
            <Typography fontSize={13} component="span">
              {" "}
              {data?.nomeCliente}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {hierarchyLabel.local}:
            <Typography fontSize={13} component="span">
              {" "}
              {data?.nomeLocal}{" "}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {hierarchyLabel.conjunto}:
            <Typography fontSize={13} component="span">
              {" "}
              {data?.nomeConjunto}
            </Typography>
          </Typography>
          <Typography fontSize={13} component="p" fontWeight={600}>
            {hierarchyLabel.unidade}:
            <Typography fontSize={13} component="span">
              {" "}
              {data?.nomeUnidade}
            </Typography>
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const Detalhes = () => {
    if (formContext) {
      return (
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
          style={{ border: '1px solid lightgray', padding: '10px' }}
          width='100%'
          spacing={1.5}
        >
          <Stack width='100%'>
            {formContext.itemNome && <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.item')}
              <Typography fontSize={13} component='span'> {formContext.itemNome}</Typography>
            </Typography>}
            {formContext.ocorrenciaNome && <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.multipleItem')}
              <Typography fontSize={13} component='span'> {formContext.ocorrenciaNome}</Typography>
            </Typography>}
            <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.Subitem_chamado')}:
              <Typography fontSize={13} component='span'> {formContext.pergunta || '-'}</Typography>
            </Typography>
            <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.Resposta_subtitem')}:
              <Typography sx={{ wordBreak: 'break-word' }} fontSize={13} component='span'> {formContext.resposta || '-'}</Typography>
            </Typography>
          </Stack>
          <Stack width='100%'>
            <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.Tipo_subitem')}: <span style={{ fontWeight: 600 }}>{tipoExibido(formContext.tipoSubitem || '')}</span>
            </Typography>
            {formContext.observacao && <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
              {t('notifications.ticket.commentLabel')}
              <Typography fontSize={13} component='span'> {formContext.observacao}</Typography>
            </Typography>}
            {formContext.imagens?.length ? <ImagesCmp images={formContext.imagens} /> : null}
          </Stack>
        </Stack>
      );
    }

    return (
      <>
        {data?.itemId
          && <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='flex-start'
            style={{ border: '1px solid lightgray', padding: '10px' }}
            width='100%'
            spacing={1.5}
          >
            <Stack width='100%'>
              <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
                {t('notifications.ticket.item')}
                <Typography fontSize={13} component='span'> {item?.nome}</Typography>
              </Typography>
              {item?.ocorrenciaMultipla && <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
                {t('notifications.ticket.multipleItem')}
                <Typography fontSize={13} component='span'> {nomeOcorrencia}</Typography>
              </Typography>}
              <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
                {t('notifications.ticket.Subitem_chamado')}:
                <Typography fontSize={13} component='span'> {subitem?.nome}</Typography>
              </Typography>
              <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
                {t('notifications.ticket.Resposta_subtitem')}: <Typography sx={{ wordBreak: 'break-word' }} fontSize={13} component='span'>
                  {subitem && renderSwitch(subitem as unknown as ChecklistSubitemReport)}
                </Typography>
              </Typography>
            </Stack>
            <Stack width='100%'>
              <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'> {t('notifications.ticket.Tipo_subitem')}:&nbsp;
                <span style={{ fontWeight: 600 }}>
                  {subitem?.tipo ? tipoExibido(subitem?.tipo) : ''}
                </span>
              </Typography>
              {subitem?.img && <ImagesCmp images={subitem.img} />}
              {subitem?.arquivos && <>
                <Typography fontSize={13} color='textDisabled' fontWeight={700} component='p'>
                  Arquivos:
                  <ListaArquivos files={subitem.arquivos} />
                </Typography>
              </>}
              {comentario(subitem)}
            </Stack>
          </Stack>
        }
        {data?.historicoChamado && <Typography variant="h6">{t("notifications.ticket.Lista_acoes")}</Typography>}
        {data?.historicoChamado &&
          Object?.keys(data?.historicoChamado as any)?.forEach((key: string) => {
          const ticketObj: any = data?.historicoChamado;
          return ticketObj[key].nomeUsuarioAcompanhamento;
          })}
      </>
    );
  };

  const onLoading = isLoading || isFetching;
  const confirmationLabels: Record<string, string> = {
    complete: "notifications.ticket.confirmActions.complete",
    cancel: "notifications.ticket.confirmActions.cancel",
    comment: "notifications.ticket.confirmActions.comment",
    priority: "notifications.ticket.confirmActions.priority",
  };

  return (
    <>
      {!requesterMode && openRedirect && <RedirectModal openRedirect={openRedirect} setOpenRedirect={closeRedirectModal} chamadoId={chamadoId} onPendingChange={setIsRedirectPending} onSuccess={onActionSuccess} />}
      {openModalPrint && <ModalDetailPrint setOpenModal={onHandleClick} openModal={openModalPrint} selected={[selected] as ChecklistReportType[]} />}
      {openImgPreview && <ModalImageOnTicket image={imgPreview} open={openImgPreview} setOpen={closeImgPreview} />}
      {!requesterMode && openRefuse && <ModalDeny openRefuse={openRefuse} setOpenRefuse={setOpenRefuse} status="recusado" disabled={isActionMutationPending} mutate={(motivo?: string) => executeAction({ actionParam: "reject", motivo })} />}
      <Dialog open={openNewTicket} onClose={() => { if (!isActionMutationPending) setOpenNewTicket(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{t("notifications.requester.openNewTicket")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ marginBottom: 2 }}>{t("notifications.requester.selectNewTicketModel")}</Typography>
          <FormControl fullWidth>
            <InputLabel id="new-ticket-model-label">{t("notifications.requester.newTicketModel")}</InputLabel>
            <Select
              labelId="new-ticket-model-label"
              value={newTicket.modeloId}
              label={t("notifications.requester.newTicketModel")}
              disabled={isActionMutationPending}
              onChange={event => setNewTicket(previous => ({ ...previous, modeloId: event.target.value }))}
            >
              {ticketModels?.map(model => (
                <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label={t("Reports.modalDetailP.callInstructions")}
            value={newTicket.instrucao ?? ""}
            disabled={isActionMutationPending}
            onChange={event => setNewTicket(previous => ({ ...previous, instrucao: event.target.value }))}
            sx={{ marginTop: 2 }}
          />
          <FormControl fullWidth sx={{ marginTop: 2 }}>
            <InputLabel id="new-ticket-priority-label">{t("Reports.modalDetailP.priority")}</InputLabel>
            <Select
              labelId="new-ticket-priority-label"
              value={newTicket.prioridade ?? ""}
              label={t("Reports.modalDetailP.priority")}
              disabled={isActionMutationPending}
              onChange={event => setNewTicket(previous => ({ ...previous, prioridade: event.target.value }))}
            >
              <MenuItem value="alta">{t("notifications.myTickets.high")}</MenuItem>
              <MenuItem value="media">{t("notifications.myTickets.medium")}</MenuItem>
              <MenuItem value="baixa">{t("notifications.myTickets.low")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button disabled={isActionMutationPending} onClick={() => setOpenNewTicket(false)}>{t("cancel")}</Button>
          <Button
            variant="contained"
            disabled={!newTicket.modeloId || isLoadingTicketModels || isActionMutationPending}
            onClick={() => createFromRejected()}
          >
            {t("notifications.requester.openNewTicket")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDispute}
        onClose={() => {
          if (!isDisputingRejected) setOpenDispute(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("notifications.ticket.actions.reopen")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={4}
            label={t("notifications.requester.disputeComment")}
            value={disputeComment}
            disabled={isActionMutationPending}
            onChange={event => setDisputeComment(event.target.value)}
            sx={{ marginTop: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isActionMutationPending} onClick={() => setOpenDispute(false)}>{t("cancel")}</Button>
          <Button
            variant="contained"
            disabled={!disputeComment.trim() || isActionMutationPending}
            onClick={() => disputeRejected()}
          >
            {t("notifications.ticket.actions.reopen")}
          </Button>
        </DialogActions>
      </Dialog>
      {onLoading ? (
        <Loading />
      ) : (
        <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={1}>
          <Buttons />
          <Stack direction="column" spacing={1} alignItems="stretch" ref={componentRef}>
            <HeaderContent />
            {data?.duplicatasLista && <ChamadosDuplicados duplicatas={data.duplicatasLista} />}
            {!requesterMode && ["pendente", "aceito", "concluido", "cancelado", "recusado", "reaberto"].includes(data?.status) && (
              <Typography variant="body2" color="text.secondary">
                {data?.status === "pendente" ? t("notifications.ticket.chamado_pendente") : t("notifications.ticket.selectAvailableAction")}
              </Typography>
            )}
            {(formContext || data?.execucaoChecklist) && <Detalhes />}
            <History />
          </Stack>
          {(canAcceptRejected || canDisputeRejected || canCreateNewTicket || canReopenCancelled) && (
            <Stack direction="row" justifyContent="center" spacing={1}>
              {canDisputeRejected && (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={isActionMutationPending}
                  onClick={() => {
                    trackEventMatomo('chamado', 'click', 'botão', 'abre contestação');
                    setOpenDispute(true);
                  }}
                >
                  {t("notifications.ticket.actions.reopen")}
                </Button>
              )}
              {canAcceptRejected && <Button
                variant="contained"
                color="success"
                disabled={isActionMutationPending}
                onClick={() => {
                  trackEventMatomo('chamado', 'click', 'botão', 'aceita chamado recusado');
                  acceptRejected();
                }}
              >
                {t("notifications.ticket.actions.close")}
              </Button>}
              {canCreateNewTicket && <Button
                variant="contained"
                color="primary"
                disabled={isActionMutationPending}
                  onClick={() => {
                    trackEventMatomo('chamado', 'click', 'botão', 'abre novo chamado');
                    const ticketData = data as TicketsType & { tipoServico?: string; prioridade?: string };
                  setNewTicket({
                    modeloId: data?.modeloId ?? "",
                    tipoServico: ticketData.tipoServico,
                    prioridade: ticketData.prioridade,
                    instrucao: ticketData.instrucao,
                    cliente: ticketData.cliente,
                    local: ticketData.local,
                    conjunto: ticketData.conjunto,
                    unidade: ticketData.unidade,
                  });
                  setOpenNewTicket(true);
                }}
              >
                {t("notifications.requester.openNewTicket")}
              </Button>}
              {canReopenCancelled && (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={isActionMutationPending}
                  onClick={() => {
                    trackEventMatomo('chamado', 'click', 'botão', 'reabre chamado cancelado');
                    reopenCancelled();
                  }}
                >
                  {t("notifications.requester.reopenCancelled")}
                </Button>
              )}
            </Stack>
          )}
          {!requesterMode && action && (
            <EmAndamento
              t={t}
              data={data}
              objectStatus={objectStatus}
              setObjectStatus={setObjectStatus}
              disable={isActionMutationPending}
              executeAction={actionParam => executeAction({ actionParam })}
              selectedAction={action}
              priority={priority}
              setPriority={setPriority}
              confirmationLabel={t(confirmationLabels[action] || "notifications.ticket.confirmActions.default")}
              verificaResponsavel={verificaResponsavel}
              arquivos={arquivos}
              setArquivos={setArquivos}
              images={images}
              setImages={setImages}
            />
          )}
          <ActionButtons />
        </Stack>
      )}
    </>
  );
}
